import React, { useState, useEffect, useRef } from 'react'
import Axios from 'axios'
import { APIProvider, Map as MapaGoogle, AdvancedMarker, Pin, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { Input, Typography } from '@material-tailwind/react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { datosDesdeLugar } from './direccionGoogle'
import {
    CENTRO_ARGENTINA, COLOR_UBICACION_MANUAL, tieneCoordenadas, posicionDeIglesia,
    estadoDeIglesia, datosDeEstado, colorDePin, describirPrecision
} from './estadosUbicacion'

const CLAVE_MAPA = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const ID_ESTILO_MAPA = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'
const MINIMO_PARA_BUSCAR = 4
const ESPERA_ANTES_DE_BUSCAR = 350
const MAXIMO_DE_SUGERENCIAS = 5

export const hayClaveGoogle = Boolean(CLAVE_MAPA)

export const ProveedorGoogle = ({ children }) => hayClaveGoogle
    ? <APIProvider apiKey={CLAVE_MAPA} language="es" region="AR">{children}</APIProvider>
    : children

export const CampoDireccionGoogle = ({ valor, verificada, alCambiarTexto, alElegirLugar }) => {
    const libreriaLugares = useMapsLibrary('places')
    const [sugerencias, setSugerencias] = useState([])
    const [abierta, setAbierta] = useState(false)
    const [resaltada, setResaltada] = useState(-1)
    const tokenDeSesion = useRef(null)
    const numeroDeBusqueda = useRef(0)
    const temporizador = useRef(null)

    useEffect(() => () => clearTimeout(temporizador.current), [])

    const cerrar = () => {
        setAbierta(false)
        setResaltada(-1)
    }

    const buscar = (texto) => {
        clearTimeout(temporizador.current)
        const numero = ++numeroDeBusqueda.current

        if (!libreriaLugares?.AutocompleteSuggestion || texto.trim().length < MINIMO_PARA_BUSCAR) {
            setSugerencias([])
            cerrar()
            return
        }

        temporizador.current = setTimeout(async () => {
            try {
                if (!tokenDeSesion.current) tokenDeSesion.current = new libreriaLugares.AutocompleteSessionToken()
                const { suggestions } = await libreriaLugares.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: texto,
                    sessionToken: tokenDeSesion.current,
                    includedRegionCodes: ['ar'],
                    language: 'es'
                })
                if (numero !== numeroDeBusqueda.current) return
                const predicciones = (suggestions || []).map(item => item.placePrediction).filter(Boolean).slice(0, MAXIMO_DE_SUGERENCIAS)
                setSugerencias(predicciones)
                setAbierta(predicciones.length > 0)
                setResaltada(-1)
            } catch (err) {
                if (numero === numeroDeBusqueda.current) {
                    setSugerencias([])
                    cerrar()
                }
            }
        }, ESPERA_ANTES_DE_BUSCAR)
    }

    const elegir = async (prediccion) => {
        cerrar()
        setSugerencias([])
        numeroDeBusqueda.current++
        try {
            const lugar = prediccion.toPlace()
            await lugar.fetchFields({ fields: ['addressComponents', 'location', 'types', 'id'] })
            tokenDeSesion.current = null
            alElegirLugar(datosDesdeLugar(lugar))
        } catch (err) {
            console.log(err)
        }
    }

    const cambiarTexto = (e) => {
        alCambiarTexto(e.target.value)
        buscar(e.target.value)
    }

    const manejarTeclas = (e) => {
        if (!abierta || !sugerencias.length) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setResaltada(previa => Math.min(previa + 1, sugerencias.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setResaltada(previa => Math.max(previa - 1, 0))
        } else if (e.key === 'Enter' && resaltada >= 0) {
            e.preventDefault()
            elegir(sugerencias[resaltada])
        } else if (e.key === 'Escape') {
            cerrar()
        }
    }

    return <div className="relative">
        <Input
            value={valor || ''}
            placeholder={'Dirección'}
            autoComplete="off"
            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
            labelProps={{
                className: "before:content-none after:content-none",
            }}
            onChange={cambiarTexto}
            onKeyDown={manejarTeclas}
            onBlur={cerrar}
        />
        {abierta && <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-blue-gray-100 rounded-md shadow-lg overflow-hidden">
            {sugerencias.map((prediccion, indice) => <li
                key={prediccion.placeId || indice}
                onMouseDown={(e) => { e.preventDefault(); elegir(prediccion) }}
                onMouseEnter={() => setResaltada(indice)}
                className={`px-3 py-2 cursor-pointer text-sm ${indice === resaltada ? 'bg-blue-50' : 'bg-white'}`}
            >
                <span className="font-medium text-blue-gray-900">{prediccion.mainText?.toString() || prediccion.text?.toString()}</span>
                {prediccion.secondaryText && <span className="block text-xs text-blue-gray-500">{prediccion.secondaryText.toString()}</span>}
            </li>)}
        </ul>}
        <div className="text-xs mt-1">
            {verificada
                ? <span className="flex items-center gap-1 text-green-700"><CheckCircleIcon className="h-4 w-4" />Dirección elegida de las sugerencias de Google</span>
                : <span className="text-blue-gray-500">Escriba la dirección y elija una de las sugerencias para que quede exacta en el mapa.</span>}
        </div>
    </div>
}

const CentrarMapa = ({ latitud, longitud }) => {
    const mapa = useMap()

    useEffect(() => {
        if (!mapa) return
        if (Number.isFinite(latitud) && Number.isFinite(longitud)) {
            mapa.panTo({ lat: latitud, lng: longitud })
            mapa.setZoom(16)
        } else {
            mapa.panTo(CENTRO_ARGENTINA)
            mapa.setZoom(4)
        }
    }, [mapa, latitud, longitud])

    return null
}

export const MapaUbicacionIglesia = ({ id_iglesia, lugarElegido, version }) => {
    const [guardada, setGuardada] = useState(null)

    useEffect(() => {
        let activo = true
        if (!id_iglesia) {
            setGuardada(null)
            return
        }
        Axios.get(`/api/ubicaciones/iglesias/${id_iglesia}`)
            .then(({ data }) => { if (activo) setGuardada(data) })
            .catch(() => { if (activo) setGuardada(null) })
        return () => { activo = false }
    }, [id_iglesia, version])

    const posicionElegida = lugarElegido ? { lat: lugarElegido.latitud, lng: lugarElegido.longitud } : null
    const posicionGuardada = guardada && tieneCoordenadas(guardada) ? posicionDeIglesia(guardada) : null
    const posicion = posicionElegida || posicionGuardada
    const color = posicionElegida ? COLOR_UBICACION_MANUAL : guardada ? colorDePin(guardada) : COLOR_UBICACION_MANUAL

    const descripcion = posicionElegida
        ? 'Ubicación nueva: se guarda al guardar la iglesia.'
        : guardada
            ? [datosDeEstado(estadoDeIglesia(guardada)).etiqueta, describirPrecision(guardada)].filter(Boolean).join(' · ')
            : id_iglesia ? 'Todavía no tiene ubicación en el mapa.' : 'La ubicación se calcula al guardar la iglesia.'

    return <div>
        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
            Ubicación en el mapa
        </Typography>
        <div className="h-64 rounded-md overflow-hidden border border-blue-gray-100">
            <MapaGoogle
                className="w-full h-full"
                mapId={ID_ESTILO_MAPA}
                defaultCenter={CENTRO_ARGENTINA}
                defaultZoom={4}
                gestureHandling="cooperative"
                disableDefaultUI={true}
                zoomControl={true}
                clickableIcons={false}
            >
                <CentrarMapa latitud={posicion?.lat} longitud={posicion?.lng} />
                {posicion && <AdvancedMarker position={posicion}>
                    <Pin background={color} borderColor="#ffffff" glyphColor="#ffffff" />
                </AdvancedMarker>}
            </MapaGoogle>
        </div>
        <span className="block text-xs mt-1 text-blue-gray-600">{descripcion}</span>
    </div>
}
